import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { NavigationStart, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { SvgIconComponent } from 'angular-svg-icon';
import { Capacitor } from '@capacitor/core';
import { CdnRegisteryService } from '@layout/services/cdn-registery.service';
import { HeaderNavService } from '@layout/services/theme/header-nav.service';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { NativeAppService } from '@common/services/native-app.service';
import { CdkConnectedOverlay, CdkOverlayOrigin, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { INavItemModel } from '@layout/models/theme/nav-item.model';
import { TemplatePortal } from '@angular/cdk/portal';
import { filter, first, Subject, takeUntil, tap } from 'rxjs';
import { LayoutWatcherService } from '@layout/services/layout-watcher.service';
import { GlobalAuthService } from 'src/app/core/auth/services/global-auth.service';
import { COLLAPSED_NAVBAR_BP } from '@layout/consts/layout-globals.const';
import { UserProfileComponent } from '../user-profile/user-profile.component';
import { RolesService } from 'src/app/core/auth/services/roles.service';

@Component({
  selector: 'mh-navbar',
  imports: [
    RouterLink,
    RouterLinkActive,
    SvgIconComponent,
    NgClass,
    NgTemplateOutlet,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    UserProfileComponent,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent implements OnInit, OnDestroy {
  // inputs
  readonly iosInsetPadding = input(0);

  // injectables
  private cdnRegisteryService = inject(CdnRegisteryService);
  private nativeAppService = inject(NativeAppService);
  private headerNavService = inject(HeaderNavService);
  private overlayService = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private layoutService = inject(LayoutWatcherService);
  private authService = inject(GlobalAuthService);
  private rolesService = inject(RolesService);
  private router = inject(Router);

  protected headerConfig = this.headerNavService.configs;
  protected headerMenu = computed(() => {
    const allMenuItems = this.headerNavService.menuItems();
    const selectedSiteObject = this.authService.selectedSiteObject();

    // Filter menu items based on selected site type
    return allMenuItems.filter((item) => {
      // PM-336 hid Developments in production builds while the feature was being
      // tested there. PM-490: prod testing is signed off, so it is visible again.
      // Note the route itself was never guarded, only this menu entry.

      // If no site is selected, show all items (except those with role restrictions)
      if (!selectedSiteObject) {
        return true;
      }

      // Properties, Developments, and Calendar should only be visible for Office sites
      const officeOnlyTabs = ['properties', 'developments', 'calendar'];
      if (officeOnlyTabs.includes(item.id)) {
        return selectedSiteObject.entityType === 'office';
      }

      // All other tabs are visible regardless of site type
      return true;
    });
  });
  protected actionMenu = this.headerNavService.actionMenuItems;

  // Collabsed menu overlay
  private readonly collabsedMenu = viewChild<TemplateRef<HTMLElement>>('collabsedMenu');
  private navbarMenuPortal: TemplatePortal<HTMLElement>;
  private overlayConfig = new OverlayConfig({
    height: '100%',
    width: '100%',
    positionStrategy: this.overlayService.position().global(),
    scrollStrategy: this.overlayService.scrollStrategies.block(),
    hasBackdrop: false,
    panelClass: 'collapased-menu-overlay',
  });
  private overlayRef: OverlayRef;

  protected isAuthenticated = computed(() => this.authService.isAuthenticated());
  protected authData = computed(() => this.authService.authData());
  protected isOfficeSite = computed(() => this.authService.selectedSiteObject()?.entityType === 'office');

  // Icons
  protected mainLogoIcon = this.cdnRegisteryService.getMedia('mainLogo');
  protected agentPortalLogoIcon = this.cdnRegisteryService.getMedia('agentPortalLogo');
  protected myHomeFavicon = this.cdnRegisteryService.getMedia('myHomeFavicon');
  protected navTogglerIcon = this.cdnRegisteryService.getMedia('navToggler');
  protected addIcon = this.cdnRegisteryService.getMedia('add');
  protected notificationIcon = this.cdnRegisteryService.getMedia('bell');
  protected chevronIcon = this.cdnRegisteryService.getMedia('chevronDown');
  protected closeBtnIcon = this.cdnRegisteryService.getMedia('close');

  private _unsubscribe = new Subject<void>();
  private _stopRouteWatcher = new Subject<void>();

  ngOnInit(): void {
    this.observeBreakpointChange();
    const selectedSite = this.authService.selectedSite();
    if (selectedSite && this.authService.isAuthenticated()) {
      this.authService.restoreSiteObject(selectedSite, this.authService.selectedSiteObject()?.entityType);
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
    this.overlayRef?.dispose();
    this._stopRouteWatcher.next();
  }

  private observeBreakpointChange(): void {
    this.layoutService.isMobile
      .pipe(
        tap((res) => {
          if (res.breakpoints[COLLAPSED_NAVBAR_BP] && this.overlayRef?.hasAttached()) {
            this.overlayRef?.detach();
          }
        }),
        takeUntil(this._unsubscribe),
      )
      .subscribe();
  }

  private watchBrowserBackbutton(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        tap(() => this.toggleCollabsedMenu()),
        first(),
        takeUntil(this._stopRouteWatcher),
      )
      .subscribe();
  }

  protected triggerSubMenu(item: INavItemModel): void {
    item.isOpen = !item.isOpen;
  }

  protected openExternalLink(url: string): void {
    this.toggleCollabsedMenu();

    if (Capacitor.isNativePlatform()) {
      this.nativeAppService.openWebView(url);
    } else {
      window.open(url, '_blank');
    }
  }

  protected toggleCollabsedMenu(): void {
    if (!this.overlayRef) {
      this.overlayRef = this.overlayService.create(this.overlayConfig);
    }

    if (!this.navbarMenuPortal && this.collabsedMenu()) {
      this.navbarMenuPortal = new TemplatePortal<HTMLElement>(this.collabsedMenu()!, this.viewContainerRef);
    }

    if (!this.overlayRef.hasAttached()) {
      this.watchBrowserBackbutton();
      this.overlayRef.attach(this.navbarMenuPortal);
      if (Capacitor.getPlatform() === 'ios') {
        this.overlayRef.overlayElement.style.setProperty('--mh-collabse-nav-safe-area', `${this.iosInsetPadding()}px`);
      }
    } else {
      this.overlayRef.detach();
      this._stopRouteWatcher.next();
    }
  }

  protected goToLogin() {
    this.authService.login();
  }

  protected showNavItem(item: INavItemModel): boolean {
    // Check if user has prohibited roles (denied takes precedence)
    if (item.permissions?.denied?.length && this.rolesService.hasRoles(item.permissions.denied)) {
      return false;
    }

    // Check if user has required roles
    if (item.permissions?.allowed?.length) {
      const hasRole = this.rolesService.hasRoles(item.permissions.allowed);
      return hasRole;
    }

    return true;
  }
}
